import React, { useState, useEffect } from 'react'
import {
  Button,
  Drawer,
  message,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  List,
  Select
} from 'antd'
import {
  EditOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  UserOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  PlusOutlined,
  MinusCircleOutlined
} from '@ant-design/icons'
import api from '../utils/api'
import styled from 'styled-components'
import dayjs from 'dayjs'
import Applicants from './Applicants'
import { useNavigate } from 'react-router-dom'
import moment from 'moment-timezone'

const { Title, Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select
const timeOptions = [
  '00:00',
  '01:00',
  '02:00',
  '03:00',
  '04:00',
  '05:00',
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00'
]

const RecruitmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const ClosedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50px;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`

const ClosedLabel = styled.div`
  background-color: #ff4d4f;
  color: white;
  padding: 3px 10px;
  transform: rotate(-15deg);
  font-weight: bold;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`

const RecruitmentCard = styled.div`
  position: relative;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 20px;
  margin: 10px;
  transition: all 0.3s;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }

  ${(props) =>
    props.closed &&
    `
    opacity: 0.7;
  `}
`

const CardTitle = styled(Title)`
  margin-top: 0 !important;
  margin-bottom: 12px !important;
`
const InfoText = styled(Text)`
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  .anticon {
    margin-right: 8px;
  }
`

const StyledTag = styled(Tag)`
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 16px;
  margin-right: 8px;
  margin-top: 8px;
  border: none;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.status-active {
    background-color: #e6f7ff;
    color: #0050b3;
  }

  &.status-closed {
    background-color: #fff1f0;
    color: #cf1322;
  }

  &.type-employee {
    background-color: #f0f5ff;
    color: #1d39c4;
  }

  &.type-experienceModel {
    background-color: #fff7e6;
    color: #d46b08;
  }

  &.type-reciprocalModel {
    background-color: #f6ffed;
    color: #389e0d;
  }
`

const ActionButton = styled(Button)`
  border-radius: 6px;

  &.ant-btn-primary {
    background-color: #1890ff;
    border-color: #1890ff;
  }

  &.ant-btn-danger {
    background-color: #ff4d4f;
    border-color: #ff4d4f;
    color: white;
  }
`

const TabButton = styled(Button)`
  margin-right: 10px;
  margin-bottom: 10px;
`

const RecruitmentList = ({ businessId }) => {
  const navigate = useNavigate()
  const [recruitments, setRecruitments] = useState([])
  const [selectedRecruitment, setSelectedRecruitment] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [applicantsDrawerVisible, setApplicantsDrawerVisible] = useState(false)
  const [
    selectedRecruitmentForApplicants,
    setSelectedRecruitmentForApplicants
  ] = useState(null)
  const [activeTab, setActiveTab] = useState('active')
  const [availableTimes, setAvailableTimes] = useState([])

  useEffect(() => {
    fetchRecruitments()
  }, [])

  const fetchRecruitments = async () => {
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/recruitments`
      )
      console.log(response.data.data)
      const recruitmentData = Array.isArray(response.data.data)
        ? response.data.data
        : [response.data.data]
      setRecruitments(recruitmentData)
    } catch (error) {
      console.log('獲取招聘信息失敗:', error)
      //   message.error('獲取招聘信息失敗')
      setRecruitments([])
    }
  }

  const showDrawer = (recruitment) => {
    setSelectedRecruitment(recruitment)
    setDrawerVisible(true)
  }

  const closeDrawer = () => {
    setDrawerVisible(false)
    setSelectedRecruitment(null)
  }

  const showEditModal = (recruitment) => {
    setSelectedRecruitment(recruitment)
    if (recruitment.recruitmentType !== 'employee') {
      setAvailableTimes(recruitment.availableTimes || [])
    }
    form.setFieldsValue({
      ...recruitment,
      dateTimeRange: recruitment.dateTimeRange
        ? [
            dayjs(recruitment.dateTimeRange.start),
            dayjs(recruitment.dateTimeRange.end)
          ]
        : undefined,
      availableTimes: undefined // 清空表單中的 availableTimes
    })
    setEditModalVisible(true)
  }

  const handleEditSubmit = async (values) => {
    try {
      const updatedRecruitment = {
        ...values,
        status: 'active'
      }

      if (selectedRecruitment.recruitmentType === 'employee') {
        updatedRecruitment.dateTimeRange = {
          start: moment(values.dateTimeRange[0]).tz('Asia/Taipei').format(),
          end: moment(values.dateTimeRange[1]).tz('Asia/Taipei').format()
        }
      } else {
        updatedRecruitment.availableTimes = availableTimes.map((slot) => ({
          date: moment(slot.date).tz('Asia/Taipei').format('YYYY-MM-DD'),
          times: slot.times
        }))
      }

      await api.put(
        `/api/business_B/${businessId}/recruitments/${selectedRecruitment._id}`,
        updatedRecruitment
      )
      message.success('招聘信息已更新並重新啟動')
      setEditModalVisible(false)
      fetchRecruitments()
    } catch (error) {
      console.error('更新招聘信息失敗:', error)
      message.error('更新招聘信息失敗')
    }
  }

  const closeRecruitment = async (id) => {
    try {
      await api.patch(`/api/business_B/${businessId}/recruitments/${id}/close`)
      message.success('招聘信息已關閉')
      fetchRecruitments()
    } catch (error) {
      message.error('關閉招聘信息失敗')
    }
  }

  const reopenRecruitment = async (id) => {
    try {
      await api.patch(`/api/business_B/${businessId}/recruitments/${id}/open`)
      message.success('招聘信息已重新啟動')
      fetchRecruitments()
    } catch (error) {
      message.error('重新啟動招聘信息失敗')
    }
  }

  const renderRecruitmentType = (type) => {
    const typeMap = {
      employee: '員工',
      experienceModel: '體驗模特',
      reciprocalModel: '互惠模特'
    }
    return typeMap[type] || type
  }

  const showApplicantsDrawer = (recruitment) => {
    setSelectedRecruitmentForApplicants(recruitment)
    setApplicantsDrawerVisible(true)
  }

  const closeApplicantsDrawer = () => {
    setApplicantsDrawerVisible(false)
    setSelectedRecruitmentForApplicants(null)
  }

  const addTimeSlot = () => {
    setAvailableTimes([...availableTimes, { date: null, times: [] }])
  }

  const removeTimeSlot = (index) => {
    const newTimes = [...availableTimes]
    newTimes.splice(index, 1)
    setAvailableTimes(newTimes)
  }

  const onDateChange = (date, index) => {
    const newTimes = [...availableTimes]
    newTimes[index].date = date ? moment(date).tz('Asia/Taipei') : null
    setAvailableTimes(newTimes)
  }

  const onTimesChange = (times, index) => {
    const newTimes = [...availableTimes]
    newTimes[index].times = times
    setAvailableTimes(newTimes)
  }

  const renderRecruitmentCard = (item) => (
    <RecruitmentCard key={item._id} closed={item.status === 'closed'}>
      {item.status === 'closed' && (
        <ClosedOverlay>
          <ClosedLabel>已關閉</ClosedLabel>
        </ClosedOverlay>
      )}
      <CardTitle level={5}>{item.title}</CardTitle>
      <InfoText>
        <CalendarOutlined />{' '}
        {item.dateTimeRange ? (
          <>
            {dayjs(item.dateTimeRange.start).format('YYYY-MM-DD')} -{' '}
            {dayjs(item.dateTimeRange.end).format('YYYY-MM-DD')}
          </>
        ) : (
          '時間未設定'
        )}
      </InfoText>
      {item.recruitmentType !== 'employee' && item.serviceProvider && (
        <InfoText>
          <UserOutlined /> {item.serviceProvider.name}
        </InfoText>
      )}
      <StyledTag className={`status-${item.status}`}>
        {item.status === 'active' ? '進行中' : '已關閉'}
      </StyledTag>
      <StyledTag className={`type-${item.recruitmentType}`}>
        {renderRecruitmentType(item.recruitmentType)}
      </StyledTag>
      <Space style={{ marginTop: 16 }}>
        <Button icon={<EditOutlined />} onClick={() => showEditModal(item)}>
          編輯
        </Button>
        <Button
          icon={<TeamOutlined />}
          onClick={() => navigate(`/applicants/${item._id}`)}
        >
          查看應聘者
        </Button>
        {item.status === 'active' ? (
          <Button
            icon={<CloseOutlined />}
            danger
            onClick={() => closeRecruitment(item._id)}
          >
            關閉
          </Button>
        ) : (
          <Button
            icon={<PlayCircleOutlined />}
            type='primary'
            onClick={() => reopenRecruitment(item._id)}
          >
            重新啟動
          </Button>
        )}
      </Space>
    </RecruitmentCard>
  )

  const renderDrawerContent = () => {
    if (!selectedRecruitment) return null

    return (
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Title level={3}>{selectedRecruitment.title}</Title>
        {selectedRecruitment.recruitmentType !== 'employee' &&
          selectedRecruitment.serviceProvider && (
            <InfoText strong>
              <UserOutlined /> 服務提供者：
              {selectedRecruitment.serviceProvider.name}
            </InfoText>
          )}
        <InfoText strong>
          招聘類型：{renderRecruitmentType(selectedRecruitment.recruitmentType)}
        </InfoText>
        <InfoText>
          <CalendarOutlined /> 時間範圍：
          {selectedRecruitment.dateTimeRange ? (
            <>
              {dayjs(selectedRecruitment.dateTimeRange.start).format(
                'YYYY-MM-DD HH:mm'
              )}{' '}
              -{' '}
              {dayjs(selectedRecruitment.dateTimeRange.end).format(
                'YYYY-MM-DD HH:mm'
              )}
            </>
          ) : (
            '時間未設定'
          )}
        </InfoText>
        <InfoText>
          <EnvironmentOutlined /> 地點：{selectedRecruitment.location.city}{' '}
          {selectedRecruitment.location.district}
        </InfoText>
        <Text>描述：{selectedRecruitment.description}</Text>
        {selectedRecruitment.recruitmentItems &&
          selectedRecruitment.recruitmentItems.length > 0 && (
            <Text>
              招聘項目：
              {selectedRecruitment.recruitmentItems
                .map((item) => item.name)
                .join(', ')}
            </Text>
          )}
        {selectedRecruitment.recruitmentType === 'experienceModel' && (
          <Text>
            體驗模特類型：
            {selectedRecruitment.experienceModelType === 'paid'
              ? '付費'
              : '免費'}
          </Text>
        )}

        <InfoText strong>
          狀態：{selectedRecruitment.status === 'active' ? '啟動' : '關閉'}
        </InfoText>
        <Space>
          {selectedRecruitment.status === 'active' ? (
            <ActionButton
              icon={<CloseOutlined />}
              danger
              onClick={() => closeRecruitment(selectedRecruitment._id)}
            >
              關閉
            </ActionButton>
          ) : (
            <ActionButton
              icon={<PlayCircleOutlined />}
              type='primary'
              onClick={() => reopenRecruitment(selectedRecruitment._id)}
            >
              重新啟動
            </ActionButton>
          )}
          <ActionButton
            icon={<EditOutlined />}
            onClick={() => showEditModal(selectedRecruitment)}
          >
            編輯
          </ActionButton>
        </Space>
      </Space>
    )
  }

  return (
    <>
      <RecruitmentContainer>
        <Space style={{ margin: 16 }}>
          <TabButton
            type={activeTab === 'active' ? 'primary' : 'default'}
            onClick={() => setActiveTab('active')}
          >
            進行中的招聘
          </TabButton>
          <TabButton
            type={activeTab === 'closed' ? 'primary' : 'default'}
            onClick={() => setActiveTab('closed')}
          >
            已關閉的招聘
          </TabButton>
        </Space>
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
          dataSource={recruitments.filter((r) => r.status === activeTab)}
          renderItem={renderRecruitmentCard}
        />
      </RecruitmentContainer>
      <Drawer
        title='招聘詳情'
        placement='right'
        onClose={closeDrawer}
        open={drawerVisible}
        width={window.innerWidth > 768 ? 520 : '100%'}
        bodyStyle={{ paddingBottom: 80 }}
      >
        {renderDrawerContent()}
      </Drawer>
      <Modal
        title='編輯招聘信息'
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={handleEditSubmit} layout='vertical'>
          <Form.Item name='title' label='標題' rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {selectedRecruitment &&
          selectedRecruitment.recruitmentType !== 'employee' ? (
            <Form.Item label='可供預約時段' required>
              {availableTimes.map((slot, index) => (
                <div key={index} style={{ marginBottom: 16 }}>
                  <DatePicker
                    value={
                      slot.date ? moment(slot.date).tz('Asia/Taipei') : null
                    }
                    onChange={(date) => onDateChange(date, index)}
                    style={{ marginRight: 8 }}
                    format='YYYY-MM-DD'
                  />
                  <Select
                    mode='multiple'
                    style={{ width: 300 }}
                    placeholder='選擇時間段'
                    value={slot.times}
                    onChange={(times) => onTimesChange(times, index)}
                  >
                    {timeOptions.map((time) => (
                      <Option key={time} value={time}>
                        {time}
                      </Option>
                    ))}
                  </Select>
                  <Button
                    type='link'
                    onClick={() => removeTimeSlot(index)}
                    icon={<MinusCircleOutlined />}
                  />
                </div>
              ))}
              <Button
                type='dashed'
                onClick={addTimeSlot}
                block
                icon={<PlusOutlined />}
              >
                添加時間段
              </Button>
            </Form.Item>
          ) : (
            <Form.Item
              name='dateTimeRange'
              label='招聘期間'
              rules={[{ required: true, message: '請選擇招聘時間範圍' }]}
            >
              <RangePicker
                showTime
                format='YYYY-MM-DD HH:mm'
                onChange={(dates) => {
                  if (dates) {
                    form.setFieldsValue({
                      dateTimeRange: dates.map((date) =>
                        moment(date).tz('Asia/Taipei')
                      )
                    })
                  }
                }}
              />
            </Form.Item>
          )}
          <Form.Item
            name={['location', 'city']}
            label='城市'
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={['location', 'district']}
            label='地區'
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name='requiredNumber'
            label='需求人數'
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={100} />
          </Form.Item>
          <Form.Item
            name='description'
            label='描述'
            rules={[{ required: true }]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              更新
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Drawer
        title='應聘者列表'
        placement='right'
        onClose={closeApplicantsDrawer}
        open={applicantsDrawerVisible}
        width={720}
      >
        {selectedRecruitmentForApplicants && (
          <Applicants
            businessId={businessId}
            recruitmentId={selectedRecruitmentForApplicants._id}
          />
        )}
      </Drawer>
    </>
  )
}

export default RecruitmentList
